import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { Spinner } from "../../components/ui/basics";
import { parseDecimalIT } from "../../utils/parseDecimalIT";

/**
 * Editor dei link mansione↔area per una mansione: righe ripetibili
 * regione→area con vincolo min h/sett XOR % incidenza. Salvataggio bulk
 * (reconcile completo per la mansione) via PUT /mansione-area/bulk.
 */
export default function LinkAreeModal({
  mansione,
  regioni,
  aree,
  onClose,
  onSaved,
}) {
  const { apiRequest } = useAuth();
  const { data: existing, loading } = useApi(
    `/normative/mansione-area?mansione=${encodeURIComponent(mansione.codice)}`,
  );
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!existing) return;
    setRows(
      existing.map((l) => ({
        regionecodice: l.regionecodice,
        areacodice: l.areacodice,
        minoresett: l.minoresett ?? "",
        incidenzapct: l.incidenzapct ?? "",
        note: l.note || "",
      })),
    );
  }, [existing]);

  const updateRow = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const addRow = () =>
    setRows((rs) => [
      ...rs,
      {
        regionecodice: "",
        areacodice: "",
        minoresett: "",
        incidenzapct: "",
        note: "",
      },
    ]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const links = rows
        .filter((r) => r.regionecodice && r.areacodice)
        .map((r) => ({
          regionecodice: r.regionecodice,
          areacodice: r.areacodice,
          minoresett: parseDecimalIT(r.minoresett),
          incidenzapct: parseDecimalIT(r.incidenzapct),
          note: r.note || null,
        }));
      await apiRequest("/normative/mansione-area/bulk", {
        method: "PUT",
        body: JSON.stringify({ mansionecodice: mansione.codice, links }),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            Link area — {mansione.name} ({mansione.codice})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        {(loading || !rows) && <Spinner />}
        {rows && (
          <form onSubmit={submit} className="p-4 space-y-3">
            <p className="text-xs text-gray-500">
              Quota del requisito d'area attribuita a questa mansione:
              valorizzare min h/sett (fisso) OPPURE % incidenza sul totale area
              — mai entrambi.
            </p>
            {rows.map((r, i) => {
              const areeRegione = aree.filter(
                (a) => a.parentcodice === r.regionecodice,
              );
              return (
                <div
                  key={i}
                  className="flex flex-wrap gap-2 items-end border border-gray-100 rounded p-2"
                >
                  <label className="text-xs">
                    <span className="block text-gray-500 mb-0.5">Regione</span>
                    <select
                      value={r.regionecodice}
                      onChange={(e) =>
                        updateRow(i, {
                          regionecodice: e.target.value,
                          areacodice: "",
                        })
                      }
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">—</option>
                      {regioni.map((reg) => (
                        <option key={reg.codice} value={reg.codice}>
                          {reg.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs">
                    <span className="block text-gray-500 mb-0.5">Area</span>
                    <select
                      value={r.areacodice}
                      onChange={(e) =>
                        updateRow(i, { areacodice: e.target.value })
                      }
                      disabled={!r.regionecodice}
                      className="border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100"
                    >
                      <option value="">—</option>
                      {areeRegione.map((a) => (
                        <option key={a.codice} value={a.codice}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs">
                    <span className="block text-gray-500 mb-0.5">
                      Min h/sett
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.minoresett}
                      onChange={(e) =>
                        updateRow(i, {
                          minoresett: e.target.value,
                          incidenzapct: "",
                        })
                      }
                      disabled={!!r.incidenzapct}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-20 disabled:bg-gray-100"
                    />
                  </label>
                  <label className="text-xs">
                    <span className="block text-gray-500 mb-0.5">
                      % incidenza
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.incidenzapct}
                      onChange={(e) =>
                        updateRow(i, {
                          incidenzapct: e.target.value,
                          minoresett: "",
                        })
                      }
                      disabled={!!r.minoresett}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-20 disabled:bg-gray-100"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    className="text-xs text-red-600 hover:underline mb-1"
                  >
                    Rimuovi
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={addRow}
              className="text-xs text-primary-600 hover:underline"
            >
              + Aggiungi link
            </button>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? "Salvataggio…" : "Salva tutti i link"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
